import React, {
    startTransition,
    useCallback,
    useEffect,
    useMemo,
    useState,
    useRef,
} from "react";
import { Base } from "../../framework/base";
import {
    getVehiclePositions,
    getVehicles,
} from "../../services/vehicleService";
import { useInterval } from "usehooks-ts";
import { WorkShiftDtoBasic } from "../../models/workShift/workShift";
import {
    getCurrentWorkShift,
    getBasicWorkShifts,
} from "../../services/workShiftService";
import {
    TransportOrderListItemDto,
    TransportOrderState,
} from "../../models/transport/transportOrder";
import { getTransportOrders } from "../../services/transportOrderService";
import { handleApiError } from "../../models/store/storeEffects";
import { useAppDispatch, useAppSelector } from "../../framework/customStore";
import { useReload } from "../../hooks";
import { MonitorViewMainLayout } from "./monitorViewMainLayout";
import { fetchEmployees as getEmployees } from "../../store/employeeSlice";
import {
    setSelectedWorkShift,
    setVehicles,
    setPositions,
} from "../../store/monitorViewSlice";
import {
    fetchWorkTimeTypes,
    setWorkTimeTypeIds,
} from "../../store/monitorViewFiltersSlice";
import {
    fetchAllVehicleGroups,
    fetchAllVehicleCameras,
} from "../../store/vehicleSlice";

const refreshInterval = 60 * 1000; // 1 minute

/** Keep only ongoing work shifts (no endTime or has open time slots) and strip ended slots. */
const filterOngoingWorkShifts = (
    shifts: WorkShiftDtoBasic[]
): WorkShiftDtoBasic[] =>
    shifts
        ?.filter(
            (shift) =>
                shift.endTime === null ||
                shift.workShiftTimeSlots?.some(
                    (slot) => slot.endDate === null
                )
        )
        ?.map((shift) => ({
            ...shift,
            workShiftTimeSlots: shift.workShiftTimeSlots.filter(
                (slot) => slot.endDate === null
            ),
        })) || [];

export const MonitorViewMain = () => {
    const dispatch = useAppDispatch();

    const workTimeTypesLoading = useAppSelector(
        (state) => state.monitorViewFilters.workTimeTypesLoading
    );
    const [key, reload] = useReload();

    const workTimeTypes = useAppSelector(
        (state) => state.monitorViewFilters.workTimeTypes
    );
    const hasInitializedFilters = useRef(false);
    const initialLoadComplete = useRef(false);

    const [ongoingWorkShifts, setOngoingWorkShifts] = useState<WorkShiftDtoBasic[] | null>(
        null
    );
    const [transportOrders, setTransportOrders] = useState<
        TransportOrderListItemDto[]
    >([]);

    const employees = useAppSelector((state) => state.employees.employees);

    const selectedVehicleId = useAppSelector(
        (state) => state.monitorView.selectedVehicleId
    );
    const selectedEmployeeId = useAppSelector(
        (state) => state.monitorView.selectedEmployeeId
    );
    const vehicles = useAppSelector(
        (state) => state.monitorView.vehicles
    );
    const positions = useAppSelector(
        (state) => state.monitorView.positions
    );

    const allVehicleGroups = useAppSelector(
        (state) => state.vehicle.allVehicleGroups
    );
    const vehicleCameras = useAppSelector(
        (state) => state.vehicle.vehicleCameras
    );

    const visibleEmployeeIds = useMemo(() => {
        return employees?.map((e) => e.id) ?? [];
    }, [employees]);

    // Fetch workshift and timeslots for each employee
    const fetchOngoingWorkShifts = useCallback(
        () => {
            if (visibleEmployeeIds.length === 0) return;

            const now = new Date();
            const startDate = Base.dateStartOf(now.addDays(-1), "month").format(
                "YYYY-MM-DD"
            );
            const endDate = Base.dateEndOf(now, "month").format("YYYY-MM-DD");

            // Get basic workshift data for overview (lighter payload than full DTO)
            getBasicWorkShifts(visibleEmployeeIds, startDate, endDate, true)
                .then((res) => {
                    startTransition(() => {
                        setOngoingWorkShifts(
                            filterOngoingWorkShifts(res?.data ?? [])
                        );
                    });
                })
                .catch((e) => {
                    handleApiError(e, dispatch);
                });
        },
        [visibleEmployeeIds, dispatch]
    );

    // Automatically toggle all filters by default when workTimeTypes are loaded
    useEffect(() => {
        if (workTimeTypes && !hasInitializedFilters.current) {
            const allWorkTimeTypeIds = workTimeTypes.map(
                (type) => type.workTimeTypeId
            );
            dispatch(setWorkTimeTypeIds(allWorkTimeTypeIds));
            hasInitializedFilters.current = true;
        }
    }, [dispatch, workTimeTypes]);

    // === Coordinated initial load ===
    // Fetches all data in phases and batches state updates to minimize render commits.
    useEffect(() => {
        let cancelled = false;

        // Reference data (independent, fire-and-forget)
        if (!workTimeTypes) dispatch(fetchWorkTimeTypes() as any);
        if (allVehicleGroups.length === 0)
            dispatch(fetchAllVehicleGroups([]) as any);
        if (!vehicleCameras) dispatch(fetchAllVehicleCameras() as any);

        const loadData = async () => {
            // Phase 1: Fetch vehicles + employees in parallel (prerequisites)
            const [vehicleRes, employeeResult] = await Promise.all([
                getVehicles(false),
                employees.length === 0
                    ? dispatch(getEmployees() as any).unwrap()
                    : Promise.resolve(employees),
            ]);

            if (cancelled) return;

            const vehicleList = vehicleRes.data;
            const empIds = (employeeResult ?? [])
                .map((e: any) => e.id)
                .filter(Boolean);
            const vehIds = vehicleList.map((v: any) => v.id);

            dispatch(setVehicles(vehicleList));

            // Phase 2: Fetch dependent data in parallel
            const now = new Date();
            const startDate = Base.dateStartOf(
                now.addDays(-1),
                "month"
            ).format("YYYY-MM-DD");
            const endDate = Base.dateEndOf(now, "month").format("YYYY-MM-DD");

            const [posRes, wsRes, toRes] = await Promise.allSettled([
                vehIds.length > 0
                    ? getVehiclePositions(vehIds, true)
                    : Promise.resolve(null),
                empIds.length > 0
                    ? getBasicWorkShifts(empIds, startDate, endDate, true)
                    : Promise.resolve(null),
                getTransportOrders({
                    selectedOrderStates: [
                        TransportOrderState.OrderCreated,
                        TransportOrderState.PickedUp,
                    ],
                    timeFrameEndDate: Base.dayjsToJsonDate(new Date()),
                    pageSize: 100,
                }),
            ]);

            if (cancelled) return;

            // Phase 3: Batch all heavy state updates in one transition
            startTransition(() => {
                if (posRes.status === "fulfilled" && posRes.value) {
                    dispatch(setPositions(posRes.value.data));
                }
                if (wsRes.status === "fulfilled" && wsRes.value) {
                    setOngoingWorkShifts(
                        filterOngoingWorkShifts(wsRes.value.data ?? [])
                    );
                }
                if (toRes.status === "fulfilled" && toRes.value) {
                    setTransportOrders(toRes.value.data?.items ?? []);
                }
            });

            initialLoadComplete.current = true;
        };

        loadData().catch((e) => {
            if (!cancelled) handleApiError(e, dispatch);
        });

        return () => {
            cancelled = true;
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Derived loading: true until initial data arrives
    const loading =
        !positions ||
        ongoingWorkShifts === null ||
        workTimeTypesLoading;

    // Re-fetch work shifts when employees change (after initial load)
    useEffect(() => {
        if (!initialLoadComplete.current) return;
        fetchOngoingWorkShifts();
    }, [fetchOngoingWorkShifts]);

    const fetchLocations = useCallback(
        () => {
            if (!selectedVehicleId && vehicles?.length > 0) {
                getVehiclePositions(vehicles.map((v) => v.id), true)
                    .then((res) => {
                        startTransition(() => {
                            dispatch(setPositions(res?.data));
                        });
                    })
                    .catch((e) => {
                        handleApiError(e, dispatch);
                    });
            }
        },
        [vehicles, selectedVehicleId, dispatch]
    );

    // Fetch workshift and workshift timeslot for employeeId that is also found in positions for the vehicle
    const fetchWorkShift = useCallback(
        () => {
            if (!!selectedEmployeeId) {
                getCurrentWorkShift(selectedEmployeeId, true)
                    .then((res) => dispatch(setSelectedWorkShift(res)))
                    .catch((e) => {
                        handleApiError(e, dispatch);
                    });
            }
        },
        [selectedEmployeeId, dispatch]
    );

    const fetchActiveTransportOrders = useCallback(() => {
        getTransportOrders({
            selectedOrderStates: [
                TransportOrderState.OrderCreated,
                TransportOrderState.PickedUp,
            ],
            timeFrameEndDate: Base.dayjsToJsonDate(new Date()),
            pageSize: 100,
        })
            .then((res) => {
                startTransition(() => {
                    setTransportOrders(res.data?.items ?? []);
                });
            })
            .catch((e) => handleApiError(e, dispatch));
    }, [dispatch]);

    const onRefresh = useCallback(
        () => {
            fetchLocations();
            fetchWorkShift();
            fetchActiveTransportOrders();
            reload();
        },
        [fetchLocations, fetchWorkShift, fetchActiveTransportOrders, reload]
    );

    // Re-fetch positions when vehicles or selectedVehicleId change (after initial load)
    useEffect(() => {
        if (!initialLoadComplete.current) return;
        fetchLocations();
    }, [fetchLocations]);

    useEffect(() => {
        fetchWorkShift();
    }, [fetchWorkShift]);

    useInterval(onRefresh, refreshInterval);

    return (
        <MonitorViewMainLayout
            positions={positions}
            loading={loading}
            workShifts={ongoingWorkShifts ?? []}
            transportOrders={transportOrders}
            reloadKey={key}
            onRefresh={onRefresh}
        />
    );
};
